import { useMemo } from "react";
// @core/analytics and trademo-tracking are untyped JS: trademoTrackingEvents(name, data)
// fans out to Pendo and Amplitude (and alerts test accounts), and
// createEventData(page, extra) stamps { page, timestamp, url, ...extra }.
import { createEventData } from "@core/analytics";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import type { ActionTrigger } from "@containers/Hermes/hooks/useActionPanel";

// Typed facade over the app's tracking (Pendo + Amplitude) for the Hermes feature. Every
// event name lives in the tracking-event dictionary and every payload is shaped
// here, so call sites stay declarative and property names can't drift. All
// events are stamped with page "Hermes" via createEventData.
export type HermesAnalytics = {
  messageSent: (p: { conversationId: string | null; isNewConversation: boolean; charLength: number }) => void;
  responseErrored: (p: { conversationId: string | null; errorCode?: string; errorMessage?: string; phase: "http" | "stream" | "network" }) => void;
  feedbackPositive: (p: { conversationId: string; messageId: number }) => void;
  feedbackNegative: (p: { conversationId: string; messageId: number; hasComment: boolean; commentLength: number }) => void;
  responseCopied: (p: { conversationId: string; messageId: number }) => void;
  suggestionClicked: (p: { suggestionType: "landing" | "followup"; text: string; position: number }) => void;
  newChatStarted: () => void;
  conversationOpened: (p: { conversationId: string }) => void;
  conversationRenamed: (p: { conversationId: string }) => void;
  conversationDeleted: (p: { conversationId: string }) => void;
  companyLinkClicked: (p: { conversationId: string | null; companyName: string; companyId: string; slug: string }) => void;
  // Fired when the user confirms a download offer — i.e. the click that spends
  // credits, not the model's decision to offer one. Carries the quoted cost so
  // offered-vs-accepted can be compared against what was actually charged.
  downloadRequested: (p: { downloadType: string; recordCount: number; credits: number; delivery: "link" | "email" }) => void;
  // Fired when the user confirms a contacts offer's persona step — the click
  // that spends value-add credits, not the model's decision to offer one.
  contactsRequested: (p: { companyCount: number; requestedContacts: number; contactType: string }) => void;
  // Fired when the user confirms a bookmark offer — the click that saves the
  // selected companies to their bookmarks.
  bookmarkRequested: (p: { companyCount: number; offeredCount: number; tagCount: number }) => void;
  // Fired when an action card opens: from the action bar, or on its own because
  // the question asked for that action. Separate from the submit events above.
  actionOpened: (p: { action: "bookmark" | "workspace" | "contacts" | "download"; trigger: ActionTrigger }) => void;
};

export default function useHermesAnalytics(): HermesAnalytics {
  return useMemo<HermesAnalytics>(() => {
    const track = (event: string, props: Record<string, unknown> = {}) => {
      trademoTrackingEvents(event, createEventData("Hermes", props));
    };

    return {
      messageSent: ({ conversationId, isNewConversation, charLength }) =>
        track(trackingEventDictionary.hermesMessageSent, {
          conversation_id: conversationId,
          is_new_conversation: isNewConversation,
          char_length: charLength,
        }),

      responseErrored: ({ conversationId, errorCode, errorMessage, phase }) =>
        track(trackingEventDictionary.hermesResponseErrored, {
          conversation_id: conversationId,
          error_code: errorCode ?? null,
          error_message: errorMessage ?? null,
          phase,
        }),

      feedbackPositive: ({ conversationId, messageId }) =>
        track(trackingEventDictionary.hermesFeedbackPositive, {
          conversation_id: conversationId,
          message_id: messageId,
        }),

      feedbackNegative: ({ conversationId, messageId, hasComment, commentLength }) =>
        track(trackingEventDictionary.hermesFeedbackNegative, {
          conversation_id: conversationId,
          message_id: messageId,
          has_comment: hasComment,
          comment_length: commentLength,
        }),

      responseCopied: ({ conversationId, messageId }) =>
        track(trackingEventDictionary.hermesResponseCopied, {
          conversation_id: conversationId,
          message_id: messageId,
        }),

      suggestionClicked: ({ suggestionType, text, position }) =>
        track(trackingEventDictionary.hermesSuggestionClicked, {
          suggestion_type: suggestionType,
          text,
          position,
        }),

      newChatStarted: () => track(trackingEventDictionary.hermesNewChatStarted),

      conversationOpened: ({ conversationId }) => track(trackingEventDictionary.hermesConversationOpened, { conversation_id: conversationId }),

      conversationRenamed: ({ conversationId }) => track(trackingEventDictionary.hermesConversationRenamed, { conversation_id: conversationId }),

      conversationDeleted: ({ conversationId }) => track(trackingEventDictionary.hermesConversationDeleted, { conversation_id: conversationId }),

      companyLinkClicked: ({ conversationId, companyName, companyId, slug }) =>
        track(trackingEventDictionary.hermesCompanyLinkClicked, {
          conversation_id: conversationId,
          company_name: companyName,
          company_id: companyId,
          slug,
        }),

      downloadRequested: ({ downloadType, recordCount, credits, delivery }) =>
        track(trackingEventDictionary.hermesDownloadRequested, {
          download_type: downloadType,
          record_count: recordCount,
          credits,
          delivery,
        }),

      contactsRequested: ({ companyCount, requestedContacts, contactType }) =>
        track(trackingEventDictionary.hermesContactsRequested, {
          company_count: companyCount,
          requested_contacts: requestedContacts,
          contact_type: contactType,
        }),

      bookmarkRequested: ({ companyCount, offeredCount, tagCount }) =>
        track(trackingEventDictionary.hermesBookmarkRequested, {
          company_count: companyCount,
          offered_count: offeredCount,
          tag_count: tagCount,
        }),

      actionOpened: ({ action, trigger }) =>
        track(
          {
            bookmark: trackingEventDictionary.hermesBookmarkOpened,
            workspace: trackingEventDictionary.hermesWorkspaceOpened,
            contacts: trackingEventDictionary.hermesContactsOpened,
            download: trackingEventDictionary.hermesDownloadOpened,
          }[action],
          { trigger }
        ),
    };
  }, []);
}
